// Importing Stimulus
import { Application } from "@hotwired/stimulus"
import { registerControllers } from "stimulus-vite-helpers"

// Configuring Stimulus
const application = Application.start()
// Keep Stimulus debug logs off by default; enable in dev only when local flag is set
// Toggle on with: localStorage.setItem('stimulusDebug','1'); and refresh
// Toggle off with: localStorage.removeItem('stimulusDebug'); and refresh
application.debug = (import.meta.env && import.meta.env.DEV === true) && (typeof localStorage !== 'undefined' && localStorage.getItem('stimulusDebug') === '1')

// Importing Stimulus controllers
const controllers = import.meta.glob("./controllers/**/*_controller.js", { eager: true })
registerControllers(application, controllers)

// components — split into "above the fold" (run immediately) and
// "lazy" (run only when each element nears the viewport). This keeps the
// main thread responsive on slow mobile CPUs where instantiating 20+
// components synchronously on DOMContentLoaded stalls paint for many seconds.
import DesktopHeader from './js/components/desktop-header';
import MobileHeader from './js/components/mobile-header';
import HeaderV3 from './js/components/header-v3';
import DesktopBlogMenu from './js/components/desktop-blog-menu';
import MobileBlogMenu from './js/components/mobile-blog-menu';
import MarqueeSlider from './js/components/marquee-slider';
import ReadingProgress from './js/components/reading-progress';
import ArticlesCarousel from './js/components/articles-carousel';
import DownloadDocument from './js/components/download-document';
import ExchangeRates from './js/components/exchange-rates';
import ExchangeRatesTable from './js/components/exchange-rates-table';
import SupportedCurrencies from './js/components/supported-currencies';
import CustomerStories from './js/components/customer-stories';
import FeaturesAccordion from './js/components/features-accordion';
import Reviews from './js/components/reviews';
import TestimonialsMultiple from './js/components/testimonials-multiple';
import HeroBubble from './js/components/hero-bubble';
import HeroFloatingParallax from './js/components/hero-floating-parallax';
import PersonalHeroParallax from './js/components/personal-hero-parallax';
import TabsCards from './js/components/tabs-cards';
import CustomerStoriesTabs from './js/components/customer-stories-tabs';
import CoinPrice from './js/components/coin-price';

import TransakWidget from './js/components/transak';
import TransakOnrampWidget from './js/components/transak-onramp';
import BuyWidgetTabs from './js/components/buy-widget-tabs';
import { initAnimations } from './js/animations';
import initHeroVideos from './js/components/hero-video';
import './js/components/gated-content';
import './js/tracking/cta';
import './js/tracking/buy-widget-funnel';

// Eager — these affect navigation/header chrome and must be available
// immediately. Keep this list small.
const eagerComponents = [
    { selector: '[data-desktop-header]', Class: DesktopHeader },
    { selector: '[data-mobile-header]', Class: MobileHeader },
    { selector: '[data-header-v3]', Class: HeaderV3 },
    { selector: '[data-desktop-blog-menu]', Class: DesktopBlogMenu },
    { selector: '[data-mobile-blog-menu]', Class: MobileBlogMenu },
    { selector: '[data-reading-progress]', Class: ReadingProgress },
    { selector: '[data-hero-bubble]', Class: HeroBubble },
    { selector: '[data-hero-floating-parallax]', Class: HeroFloatingParallax },
    { selector: '[data-personal-hero-parallax]', Class: PersonalHeroParallax },
];

// Lazy — these are inside content blocks further down the page. We only
// instantiate them when the element approaches the viewport.
const lazyComponents = [
    { selector: '[data-marquee-slider]', Class: MarqueeSlider },
    { selector: '[data-articles-carousel]', Class: ArticlesCarousel },
    { selector: '[data-download-document]', Class: DownloadDocument },
    { selector: '[data-exchange-rates]', Class: ExchangeRates },
    { selector: '[data-exchange-rates-table]', Class: ExchangeRatesTable },
    { selector: '[data-supported-currencies]', Class: SupportedCurrencies },
    { selector: '[data-customer-stories]', Class: CustomerStories },
    { selector: '[data-features-accordion]', Class: FeaturesAccordion },
    { selector: '[data-reviews]', Class: Reviews },
    { selector: '[data-testimonials-multiple]', Class: TestimonialsMultiple },
    { selector: '[data-tabs-cards-block]', Class: TabsCards },
    { selector: '[data-customer-stories-block]', Class: CustomerStoriesTabs },
    { selector: '[data-coin-price-block]', Class: CoinPrice },
    { selector: '[data-transak-widget]', Class: TransakWidget },
    { selector: '[data-transak-onramp-widget]', Class: TransakOnrampWidget },
    { selector: '[data-buy-widget-tabs]', Class: BuyWidgetTabs },
];

// Instantiate a list of components without blocking the main thread.
// Each component init is yielded between via requestAnimationFrame so the
// browser can paint, handle input, and lazy-load images in between.
function instantiateInChunks(list) {
    if (!list.length) return;
    const [first, ...rest] = list;
    try { new first.Class(first.el); } catch (e) { /* ignore individual failures */ }
    if (rest.length) {
        requestAnimationFrame(() => instantiateInChunks(rest));
    }
}

function collectMatches(components) {
    const out = [];
    components.forEach(({ selector, Class }) => {
        document.querySelectorAll(selector).forEach(el => {
            out.push({ Class, el });
        });
    });
    return out;
}

function initLazyComponents() {
    // IntersectionObserver instantiates each component when its element
    // gets near the viewport. rootMargin keeps init slightly ahead of
    // scroll so behavior feels instant.
    if (!('IntersectionObserver' in window)) {
        // Fallback for very old browsers — init everything, but still
        // chunked so we don't block the main thread.
        instantiateInChunks(collectMatches(lazyComponents));
        return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            const match = lazyComponents.find(c => el.matches(c.selector));
            if (match) {
                try { new match.Class(el); } catch (e) {}
            }
            obs.unobserve(el);
        });
    }, { rootMargin: '300px 0px', threshold: 0 });

    lazyComponents.forEach(({ selector }) => {
        document.querySelectorAll(selector).forEach(el => observer.observe(el));
    });
}

function boot() {
    // 1. Hero/header components first — chunked to avoid jank, but started
    // immediately because they affect what's above the fold.
    instantiateInChunks(collectMatches(eagerComponents));

    // 2. Hero video lazy-loading — cheap, always run.
    initHeroVideos();

    // 3. Below-the-fold components — observed, instantiated on demand.
    initLazyComponents();

    // 4. Animations — already gated on width >= 768 and on the presence
    // of [data-animate] / [data-parallax] elements. Defer until the
    // browser is idle so it never competes with first paint.
    const startAnimations = () => initAnimations();
    if ('requestIdleCallback' in window) {
        requestIdleCallback(startAnimations, { timeout: 2000 });
    } else {
        setTimeout(startAnimations, 200);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
